import {Moment} from 'moment-timezone'

import type {OptimizedSchedule, Schedule} from '../api'

import {useCurrentScheduleQuery} from './useCurrentScheduleQuery'
import {useOptimizedScheduleQuery} from './useOptimizedScheduleQuery'

interface UseScheduleQueryParams {
  plantId: string
  scheduleId?: number | undefined
  throwOnError?: boolean
  range: [Moment, Moment]
  /**
   * Optional side-effect hook invoked by the consumer, e.g.
   * `usePeriodicAutofillCheck`, to reload the schedule when an autofill run
   * completes. Kept as a param so the domain layer stays free of
   * feature-specific logic. Must be a stable reference across renders.
   */
  useSideEffect?: () => void
}

const noop = (): void => undefined

interface UseOptimizedScheduleQueryResult {
  data: OptimizedSchedule | undefined
  isOptimized: true
  isFetching: boolean
}
interface UseCurrentScheduleQueryResult {
  data: Schedule | undefined
  isOptimized: false
  isFetching: boolean
}

export type UseScheduleQueryResult<T extends number | undefined> = T extends number
  ? UseOptimizedScheduleQueryResult
  : UseCurrentScheduleQueryResult

export const useScheduleQuery = <T extends number | undefined>({
  plantId,
  scheduleId,
  throwOnError = true,
  range,
  useSideEffect = noop
}: UseScheduleQueryParams): UseScheduleQueryResult<T> => {
  // Consumer-provided side effect, e.g. reload the schedule on autofill status change
  useSideEffect()

  const {data: currentSchedule, isFetching: isCurrentScheduleFetching} = useCurrentScheduleQuery({
    plantId,
    range
  })

  const {data: optimizedSchedule, isFetching: isOptimizedScheduleFetching} =
    useOptimizedScheduleQuery({
      plantId,
      scheduleId,
      enabled: scheduleId !== undefined,
      throwOnError,
      range
    })

  return {
    isOptimized: scheduleId !== undefined,
    data: scheduleId !== undefined ? optimizedSchedule : currentSchedule,
    isFetching: isCurrentScheduleFetching || isOptimizedScheduleFetching
  } as UseScheduleQueryResult<T>
}
